import { RemoteClient } from '@yoti-web-share/bridge'
import * as Resources from './resources'
import * as Commands from './commands'
import ClientConnectionError from './errors/ClientConnectionError'
import { logger } from './logger'

class IframeClient extends RemoteClient {
  async connect() {
    const MAX_ATTEMPT = 3
    const DELAY_BASE = 1000
    let attemptCount = 0
    const logPrefix = `IframeClient (${this.name})`

    const attemptConnection = () => {
      attemptCount += 1
      logger.log(`${logPrefix} attempting to connect ${attemptCount}/${MAX_ATTEMPT}`)

      return super.connect((2 ** attemptCount) * DELAY_BASE)
        .then((data) => {
          logger.log(`${logPrefix} is connected!`)
          return data
        })
        .catch((err) => {
          logger.warn(`${logPrefix} connection error:`, err)
          if (attemptCount < MAX_ATTEMPT) {
            return attemptConnection()
          }
          logger.error(`${logPrefix} could not connect after ${MAX_ATTEMPT} attempts, last error:`, err)
          throw new ClientConnectionError()
        })
    }

    return attemptConnection()
  }


  rejectIfNotConnected() {
    if (!this.connected) {
      return Promise.reject(new Error('Client is not connected'))
    }
    return undefined
  }

  getConfiguration() {
    return this.rejectIfNotConnected()
      || this.sendDataRequest(Resources.CONFIGURATION)
        .then(({ data }) => data)
  }

  getContext() {
    return this.rejectIfNotConnected()
      || this.sendDataRequest(Resources.CONTEXT)
        .then(({ data }) => data)
  }

  sendLaunch() {
    return this.rejectIfNotConnected()
    || this.sendCommandRequest(Commands.LAUNCH_IFRAME)
      .then(({ result }) => result)
  }

  sendClose() {
    return this.rejectIfNotConnected()
    || this.sendCommandRequest(Commands.CLOSE_IFRAME)
      .then(({ result }) => result)
  }

  requestWS(url) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.GET_WEBSOCKET, { url })
        .then(({ result }) => result)
  }

  requestWSReadyState(socketId) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.GET_WEBSOCKET_READY_STATE, { socketId })
        .then(({ result }) => result)
  }

  sendWSMessage(socketId, message) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.SEND_WEBSOCKET_MESSAGE, { socketId, message })
        .then(({ result }) => result)
  }

  closeWS(socketId) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.REMOVE_WEBSOCKET, { socketId })
        .then(({ result }) => result)
  }

  sendDimensions({ width, height }) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.SET_DIMENSIONS, { width, height })
        .then(({ result }) => result)
  }

  openSimpleLink({ href, target }) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(Commands.OPEN_SIMPLE_LINK, { href, target })
        .then(({ result }) => result)
  }

  getResource(resource) {
    return this.rejectIfNotConnected()
      || this.sendDataRequest(resource)
        .then(({ data }) => data)
  }

  commandTo(commandName, commandOptions) {
    return this.rejectIfNotConnected()
      || this.sendCommandRequest(commandName, commandOptions)
        .then(({ result }) => result)
  }
}

export default IframeClient
